import React from 'react';
import { SearchFacet, RemoveFilterCallback } from '../../../types/search';
import { Button } from 'react-aria-components';
import { parseDate, DateFormatter } from '@internationalized/date';
import { CALENDAR_DATE_FACET, CALENDAR_RESULTS_DATE_FACET } from '../index';
import clsx from 'clsx';

interface PortalAppliedFiltersProps {
  appliedFilters: SearchFacet[];
  hasFiltersApplied: boolean;
  removeFilter: RemoveFilterCallback;
  listingType?: string;
  showSeparator?: boolean;
  disabled?: boolean;
}

export default function PortalAppliedFilters({
  appliedFilters,
  hasFiltersApplied,
  removeFilter,
  listingType,
  showSeparator = false,
  disabled = false,
}: Readonly<PortalAppliedFiltersProps>) {
  if (!hasFiltersApplied) return null;

  // Filter out date-related facets for calendar view (jobs) as they are controlled by calendar cards
  const isCalendarView = listingType === 'jobs';
  const filteredAppliedFilters = isCalendarView
    ? appliedFilters.filter(facet => {
        // Exclude standard date keys
        if (facet.key === 'dateFrom' || facet.key === 'dateTo') return false;
        // Exclude calendar date facet keys
        if (CALENDAR_DATE_FACET && facet.key === CALENDAR_DATE_FACET) return false;
        if (CALENDAR_RESULTS_DATE_FACET && facet.key === CALENDAR_RESULTS_DATE_FACET) return false;
        // Exclude any facet with date type
        if (facet.type === 'date') return false;
        return true;
      })
    : appliedFilters;

  // Check if there are any filters to display after filtering
  const hasVisibleFilters = filteredAppliedFilters.some(facet => {
    // Skip status facets as they're not shown in applied filters
    if (facet.key === 'status') {
      return false;
    }
    if (facet.type === 'date') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(facet.key) !== null;
    }
    return facet.items.some(item => item.isSelected);
  });

  if (!hasVisibleFilters) return null;

  function formatDateRange(dateString: string): string {
    try {
      const dates = dateString.split(',');
      if (dates.length < 2) return dateString;

      const formatter = new DateFormatter('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

      const startDate = parseDate(dates[0]);
      const endDate = parseDate(dates[1]);

      return `${formatter.format(startDate.toDate('UTC'))} - ${formatter.format(endDate.toDate('UTC'))}`;
    } catch {
      return dateString;
    }
  }

  return (
    <div
      className={clsx('dynamic-filters__applied-filters', {
        'dynamic-filters__applied-filters--with-separator': showSeparator,
      })}
    >
      {filteredAppliedFilters.map(facet => {
        // Handle date facets differently - they have date values in items but no isSelected flag
        if (facet.type === 'date') {
          // Get date value from URL since it's not marked as selected in the facet
          const urlParams = new URLSearchParams(globalThis.location.search);
          const dateValue = urlParams.get(facet.key);
          if (!dateValue) return null;

          return (
            <Button
              className="dynamic-filters__applied-filter-label"
              key={`${facet.key}-${dateValue}`}
              aria-label={`Remove ${facet.heading} filter`}
              onClick={() => removeFilter({ facetKey: facet.key, itemValue: dateValue })}
              type="button"
              isDisabled={disabled}
            >
              {facet.heading}: {formatDateRange(dateValue)}
              {!disabled && (
                <svg className="dynamic-filters__remove-applied-filter-icon icon" aria-hidden="true">
                  <use href="/dist/assets/icons/sprite.svg#x-mark-medium"></use>
                </svg>
              )}
            </Button>
          );
        }

        // Handle regular facets with isSelected items
        // Skip status facets as they have their own selected state in quick filters
        if (facet.key === 'status') {
          return null;
        }

        return facet.items
          .filter(item => item.isSelected)
          .map(item => (
            <Button
              className="dynamic-filters__applied-filter-label"
              key={`${facet.key}-${item.value}`}
              aria-label={`Remove ${item.label || item.value} filter`}
              onClick={() => removeFilter({ facetKey: facet.key, itemValue: item.value })}
              type="button"
              isDisabled={disabled}
            >
              {item.label || item.value}
              {!disabled && (
                <svg className="dynamic-filters__remove-applied-filter-icon icon" aria-hidden="true">
                  <use href="/dist/assets/icons/sprite.svg#x-mark-medium"></use>
                </svg>
              )}
            </Button>
          ));
      })}
    </div>
  );
}
